import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';

interface StoreConfig {
  id: string;
  brand_id: string;
  brand_name: string;
  brand_slug: string;
  brand_color: string;
  name: string;
  store_code: string;
  address: string;
  city: string;
  state: string;
  country: string;
  timezone: string;
  layout: Record<string, string>;
  colleagues: Array<{ name: string; role: string }>;
  flowsEnabled: string[];
  hours: Record<string, string>;
  pin_enabled: number;
  pin_greeting: string;
  productLocations: Array<{
    id: string;
    product_category: string;
    product_name: string;
    location_description: string;
    section: string;
  }>;
}

export const Route = createFileRoute('/stores_/store_/$id')({
  component: StoreDetailPage,
});

function StoreDetailPage() {
  const { id } = Route.useParams();
  const [store, setStore] = useState<StoreConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'layout' | 'pin'>('general');

  // Editable fields
  const [name, setName] = useState('');
  const [storeCode, setStoreCode] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pinEnabled, setPinEnabled] = useState(true);
  const [pinGreeting, setPinGreeting] = useState('');
  const [flowsEnabled, setFlowsEnabled] = useState<string[]>([]);
  const [colleagues, setColleagues] = useState<Array<{ name: string; role: string }>>([]);
  const [productLocations, setProductLocations] = useState<Array<{
    productCategory: string;
    productName: string;
    locationDescription: string;
    section: string;
  }>>([]);

  // New location form
  const [newLocCategory, setNewLocCategory] = useState('');
  const [newLocProduct, setNewLocProduct] = useState('');
  const [newLocDesc, setNewLocDesc] = useState('');
  const [newLocSection, setNewLocSection] = useState('');

  // New colleague form
  const [newColleagueName, setNewColleagueName] = useState('');
  const [newColleagueRole, setNewColleagueRole] = useState('');

  useEffect(() => {
    loadStore();
  }, [id]);

  const loadStore = async () => {
    try {
      const response = await fetch(`/api/v1/config/stores/${id}`);
      if (response.ok) {
        const data = await response.json();
        setStore(data);
        setName(data.name || '');
        setStoreCode(data.store_code || '');
        setAddress(data.address || '');
        setCity(data.city || '');
        setState(data.state || '');
        setPinEnabled(!!data.pin_enabled);
        setPinGreeting(data.pin_greeting || '');
        setFlowsEnabled(data.flowsEnabled || []);
        setColleagues(data.colleagues || []);
        setProductLocations(
          (data.productLocations || []).map((loc: any) => ({
            productCategory: loc.product_category,
            productName: loc.product_name,
            locationDescription: loc.location_description,
            section: loc.section,
          }))
        );
      }
    } catch (error) {
      console.error('Failed to load store:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const response = await fetch(`/api/v1/config/stores/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          storeCode,
          address,
          city,
          state,
          pinEnabled,
          pinGreeting,
          flowsEnabled,
          colleagues,
          productLocations,
        }),
      });

      if (response.ok) {
        loadStore();
      }
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleFlow = (flow: string) => {
    if (flowsEnabled.includes(flow)) {
      setFlowsEnabled(flowsEnabled.filter((f) => f !== flow));
    } else {
      setFlowsEnabled([...flowsEnabled, flow]);
    }
  };

  const addLocation = () => {
    if (newLocCategory && newLocDesc) {
      setProductLocations([
        ...productLocations,
        {
          productCategory: newLocCategory,
          productName: newLocProduct,
          locationDescription: newLocDesc,
          section: newLocSection,
        },
      ]);
      setNewLocCategory('');
      setNewLocProduct('');
      setNewLocDesc('');
      setNewLocSection('');
    }
  };

  const removeLocation = (index: number) => {
    setProductLocations(productLocations.filter((_, i) => i !== index));
  };

  const addColleague = () => {
    if (newColleagueName && newColleagueRole) {
      setColleagues([...colleagues, { name: newColleagueName, role: newColleagueRole }]);
      setNewColleagueName('');
      setNewColleagueRole('');
    }
  };

  const removeColleague = (index: number) => {
    setColleagues(colleagues.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: '#00FBA9' }} />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="max-w-4xl p-12 text-center">
        <p className="mb-4" style={{ color: '#A9A9A9' }}>Store not found</p>
        <Link to="/stores" className="text-sm" style={{ color: '#00FBA9' }}>Back to Stores</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Link to="/stores" className="text-sm flex items-center gap-1 mb-4" style={{ color: '#A9A9A9' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to Store Configuration
        </Link>
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 flex items-center justify-center"
            style={{ backgroundColor: store.brand_color }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-light" style={{ color: '#171717' }}>{store.name}</h1>
            <p className="text-sm" style={{ color: '#A9A9A9' }}>{store.brand_name} • {store.city}, {store.state}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
        <button
          onClick={() => setActiveTab('general')}
          className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
          style={{
            borderColor: activeTab === 'general' ? '#00FBA9' : 'transparent',
            color: activeTab === 'general' ? '#171717' : '#A9A9A9',
          }}
        >
          General
        </button>
        <button
          onClick={() => setActiveTab('layout')}
          className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
          style={{
            borderColor: activeTab === 'layout' ? '#00FBA9' : 'transparent',
            color: activeTab === 'layout' ? '#171717' : '#A9A9A9',
          }}
        >
          Store Layout
        </button>
        <button
          onClick={() => setActiveTab('pin')}
          className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
          style={{
            borderColor: activeTab === 'pin' ? '#00FBA9' : 'transparent',
            color: activeTab === 'pin' ? '#171717' : '#A9A9A9',
          }}
        >
          PIN Settings
        </button>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h2 className="font-medium mb-4" style={{ color: '#171717' }}>Store Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Store Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Store Code</label>
                <input
                  type="text"
                  value={storeCode}
                  onChange={(e) => setStoreCode(e.target.value)}
                  className="w-full px-3 py-2 border"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  placeholder="e.g., NJ-001"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Address</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full px-3 py-2 border"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>City</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-3 py-2 border"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>State</label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full px-3 py-2 border"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                />
              </div>
            </div>
          </div>

          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h2 className="font-medium mb-4" style={{ color: '#171717' }}>Store Colleagues</h2>
            <p className="text-sm mb-4" style={{ color: '#A9A9A9' }}>
              The AI will mention these colleagues by name when offering to get items
            </p>
            <div className="space-y-2">
              {colleagues.map((colleague, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50">
                  <div>
                    <span className="font-medium text-sm" style={{ color: '#171717' }}>{colleague.name}</span>
                    <span className="text-sm ml-2" style={{ color: '#A9A9A9' }}>({colleague.role})</span>
                  </div>
                  <button
                    onClick={() => removeColleague(index)}
                    className="text-xs px-2 py-1 rounded"
                    style={{ color: '#FF4C00' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t flex gap-3" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
              <input
                type="text"
                value={newColleagueName}
                onChange={(e) => setNewColleagueName(e.target.value)}
                className="w-32 px-3 py-2 border text-sm"
                style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                placeholder="Name"
              />
              <select
                value={newColleagueRole}
                onChange={(e) => setNewColleagueRole(e.target.value)}
                className="px-3 py-2 border text-sm"
                style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              >
                <option value="">Select role</option>
                <option value="educator">Educator</option>
                <option value="key leader">Key Leader</option>
                <option value="manager">Manager</option>
                <option value="assistant">Assistant</option>
              </select>
              <button
                onClick={addColleague}
                disabled={!newColleagueName || !newColleagueRole}
                className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: '#FAFAFA', color: '#171717', border: '1px solid rgba(169,169,169,0.3)' }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Layout Tab */}
      {activeTab === 'layout' && (
        <div className="space-y-6">
          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <h2 className="font-medium mb-4" style={{ color: '#171717' }}>Product Locations</h2>
            <p className="text-sm mb-4" style={{ color: '#A9A9A9' }}>
              Tell the AI where products are located so it can give directions
            </p>
            <div className="space-y-2">
              {productLocations.map((loc, index) => (
                <div key={index} className="flex items-start justify-between p-3 bg-gray-50">
                  <div>
                    <span className="font-medium text-sm" style={{ color: '#171717' }}>
                      {loc.productName || loc.productCategory}
                    </span>
                    {loc.productName && (
                      <span className="text-xs ml-2" style={{ color: '#A9A9A9' }}>({loc.productCategory})</span>
                    )}
                    <p className="text-sm" style={{ color: '#A9A9A9' }}>{loc.locationDescription}</p>
                    {loc.section && (
                      <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(0,251,169,0.15)', color: '#00FBA9' }}>
                        {loc.section}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeLocation(index)}
                    className="text-xs px-2 py-1 rounded"
                    style={{ color: '#FF4C00' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: '#171717' }}>Add Location</h3>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={newLocCategory}
                  onChange={(e) => setNewLocCategory(e.target.value)}
                  className="px-3 py-2 border text-sm"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  placeholder="Category (e.g., leggings)"
                />
                <input
                  type="text"
                  value={newLocProduct}
                  onChange={(e) => setNewLocProduct(e.target.value)}
                  className="px-3 py-2 border text-sm"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  placeholder="Product name (optional)"
                />
                <input
                  type="text"
                  value={newLocDesc}
                  onChange={(e) => setNewLocDesc(e.target.value)}
                  className="col-span-2 px-3 py-2 border text-sm"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  placeholder="Location description (e.g., Left wall near entrance)"
                />
                <select
                  value={newLocSection}
                  onChange={(e) => setNewLocSection(e.target.value)}
                  className="px-3 py-2 border text-sm"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                >
                  <option value="">Section (optional)</option>
                  <option value="entrance">Entrance</option>
                  <option value="front">Front</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="center">Center</option>
                  <option value="back">Back</option>
                </select>
                <button
                  onClick={addLocation}
                  disabled={!newLocCategory || !newLocDesc}
                  className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: '#00FBA9', color: '#171717' }}
                >
                  Add Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PIN Settings Tab */}
      {activeTab === 'pin' && (
        <div className="space-y-6">
          <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium" style={{ color: '#171717' }}>Physical Intelligence Node</h2>
              <button
                onClick={() => setPinEnabled(!pinEnabled)}
                className="relative w-12 h-6 rounded-full transition-colors"
                style={{ backgroundColor: pinEnabled ? '#00FBA9' : '#A9A9A9' }}
              >
                <span
                  className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
                  style={{ left: pinEnabled ? '26px' : '4px' }}
                />
              </button>
            </div>
            <p className="text-sm mb-4" style={{ color: '#A9A9A9' }}>
              {pinEnabled ? 'PIN is enabled for this store' : 'PIN is disabled for this store'}
            </p>
          </div>

          {pinEnabled && (
            <>
              <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
                <h2 className="font-medium mb-4" style={{ color: '#171717' }}>Custom Greeting</h2>
                <textarea
                  value={pinGreeting}
                  onChange={(e) => setPinGreeting(e.target.value)}
                  className="w-full px-3 py-2 border min-h-[80px]"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  placeholder="Hello! Welcome to our store. I'm here to help you find exactly what you need."
                />
                <p className="text-xs mt-1" style={{ color: '#A9A9A9' }}>
                  Leave empty to use the brand default greeting
                </p>
              </div>

              <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
                <h2 className="font-medium mb-4" style={{ color: '#171717' }}>Enabled Flows</h2>
                <div className="space-y-3">
                  {[
                    { id: 'voice_assist', name: 'Voice Assistant', desc: 'AI Educator for product questions' },
                    { id: 'takeback', name: 'Trade-In', desc: 'Like New trade-in flow' },
                    { id: 'browse', name: 'Browse Products', desc: 'Product catalog browsing' },
                  ].map((flow) => (
                    <label
                      key={flow.id}
                      className="flex items-center justify-between p-3 cursor-pointer"
                      style={{ backgroundColor: flowsEnabled.includes(flow.id) ? 'rgba(0,251,169,0.1)' : '#FAFAFA' }}
                    >
                      <div>
                        <p className="font-medium text-sm" style={{ color: '#171717' }}>{flow.name}</p>
                        <p className="text-xs" style={{ color: '#A9A9A9' }}>{flow.desc}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={flowsEnabled.includes(flow.id)}
                        onChange={() => toggleFlow(flow.id)}
                        className="w-5 h-5 accent-[#00FBA9]"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={saveChanges}
          disabled={saving}
          className="px-6 py-3 rounded-full text-sm font-medium disabled:opacity-50"
          style={{ backgroundColor: '#00FBA9', color: '#171717' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

